// OtohikariGlobe.js (JavaScript / JSX)
// 必要:
//   npm i three topojson-client firebase
//
// 追加:
//   public/data/land-110m.json を配置
//
// 使い方例:
//   <OtohikariGlobe
//     height={820}
//     autoRotate
//     showDebugControls={false}
//     db={db}
//     trackId={selectedTrackId}
//   />

import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { feature, mesh } from 'topojson-client';
import { doc, getDoc } from 'firebase/firestore';
import { useDayBoundary } from '../context/DayBoundaryContext';

const PREFECTURE_COORDS = {
  '北海道': { lat: 43.0642, lng: 141.3469 },
  '青森県': { lat: 40.8244, lng: 140.7400 },
  '岩手県': { lat: 39.7036, lng: 141.1527 },
  '宮城県': { lat: 38.2682, lng: 140.8694 },
  '秋田県': { lat: 39.7186, lng: 140.1024 },
  '山形県': { lat: 38.2404, lng: 140.3633 },
  '福島県': { lat: 37.7503, lng: 140.4676 },
  '茨城県': { lat: 36.3418, lng: 140.4468 },
  '栃木県': { lat: 36.5658, lng: 139.8836 },
  '群馬県': { lat: 36.3911, lng: 139.0608 },
  '埼玉県': { lat: 35.8570, lng: 139.6489 },
  '千葉県': { lat: 35.6050, lng: 140.1233 },
  '東京都': { lat: 35.6895, lng: 139.6917 },
  '神奈川県': { lat: 35.4478, lng: 139.6425 },
  '新潟県': { lat: 37.9026, lng: 139.0232 },
  '富山県': { lat: 36.6953, lng: 137.2113 },
  '石川県': { lat: 36.5944, lng: 136.6256 },
  '福井県': { lat: 36.0652, lng: 136.2216 },
  '山梨県': { lat: 35.6639, lng: 138.5683 },
  '長野県': { lat: 36.6513, lng: 138.1810 },
  '岐阜県': { lat: 35.3912, lng: 136.7223 },
  '静岡県': { lat: 34.9769, lng: 138.3831 },
  '愛知県': { lat: 35.1802, lng: 136.9066 },
  '三重県': { lat: 34.7303, lng: 136.5086 },
  '滋賀県': { lat: 35.0045, lng: 135.8686 },
  '京都府': { lat: 35.0212, lng: 135.7556 },
  '大阪府': { lat: 34.6863, lng: 135.5200 },
  '兵庫県': { lat: 34.6913, lng: 135.1830 },
  '奈良県': { lat: 34.6851, lng: 135.8048 },
  '和歌山県': { lat: 34.2260, lng: 135.1675 },
  '鳥取県': { lat: 35.5037, lng: 134.2383 },
  '島根県': { lat: 35.4723, lng: 133.0505 },
  '岡山県': { lat: 34.6618, lng: 133.9344 },
  '広島県': { lat: 34.3963, lng: 132.4596 },
  '山口県': { lat: 34.1859, lng: 131.4714 },
  '徳島県': { lat: 34.0658, lng: 134.5593 },
  '香川県': { lat: 34.3401, lng: 134.0434 },
  '愛媛県': { lat: 33.8416, lng: 132.7657 },
  '高知県': { lat: 33.5597, lng: 133.5311 },
  '福岡県': { lat: 33.6064, lng: 130.4181 },
  '佐賀県': { lat: 33.2494, lng: 130.2988 },
  '長崎県': { lat: 32.7448, lng: 129.8737 },
  '熊本県': { lat: 32.7898, lng: 130.7417 },
  '大分県': { lat: 33.2382, lng: 131.6126 },
  '宮崎県': { lat: 31.9111, lng: 131.4239 },
  '鹿児島県': { lat: 31.5602, lng: 130.5581 },
  '沖縄県': { lat: 26.2124, lng: 127.6809 },
};

// getTodayKeyJST は DayBoundaryContext 経由（コンポーネント内で useDayBoundary()）

function computeLightCount(count) {
  if (!count || count <= 0) return 0;
  return Math.max(1, Math.min(18, Math.ceil(Math.sqrt(count) / 2.2)));
}

function jitter(value, range) {
  return value + (Math.random() - 0.5) * range;
}

function buildPrefectureSeeds(byPref = {}) {
  return Object.entries(byPref)
    .filter(([pref, count]) => PREFECTURE_COORDS[pref] && Number(count) > 0)
    .map(([pref, count]) => ({
      pref,
      count: Number(count),
      ...PREFECTURE_COORDS[pref],
    }));
}

// ── 光のプリセット ──────────────────────────────────────
const LIGHT_PRESETS = [
  {
    name: 'Stardust',
    pointSize: 6,
    sizeRandom: 4,
    pulseBase: 0.2,
    pulseAmp: 0.8,
    pulseSpeed: 3.0,
    glowRadius: 0.45,
    coreRadius: 0.02,
    outerColor: [0.9, 0.9, 1.0],
    innerColor: [1.0, 1.0, 1.0],
    opacity: 1.0,
    additive: true,
  },
  {
    name: 'Firefly',
    pointSize: 3,
    sizeRandom: 1,
    pulseBase: 1.0,
    pulseAmp: 0.0,
    pulseSpeed: 0.0,
    glowRadius: 0.2,
    coreRadius: 0.01,
    outerColor: [0.8, 0.9, 1.0],
    innerColor: [1.0, 1.0, 1.0],
    opacity: 0.9,
    additive: false,
  },
  {
    name: 'Pinlight',
    pointSize: 2,
    sizeRandom: 0.5,
    pulseBase: 1.0,
    pulseAmp: 0.0,
    pulseSpeed: 0.0,
    glowRadius: 0.08,
    coreRadius: 0.04,
    outerColor: [0.7, 0.85, 1.0],
    innerColor: [1.0, 1.0, 1.0],
    opacity: 0.45,
    additive: false,
  },
];

export default function OtohikariGlobe({
  height = 720,
  autoRotate = true,
  background = '#01030a',
  worldAtlasUrl = '/data/land-110m.json',
  showDebugControls = false,
  isAdmin = false,
  db,
  trackId,
  aggregateCollectionName = 'musicDailyAggregates',
  pollSec = 0,
  burstMin = 0,
  currentListeners = 0,
  todayListeners = 0,
}) {
  const { buildDateKey } = useDayBoundary();
  const buildDateKeyRef = useRef(buildDateKey);
  buildDateKeyRef.current = buildDateKey;

  const mountRef = useRef(null);
  const wrapperRef = useRef(null);
  const firefliesRef = useRef(null);
  const fireflyMaterialRef = useRef(null);
  const lastSeedsRef = useRef({ byPref: {}, byLocation: {} });
  const presetIndexRef = useRef(0);

  const [resolvedHeight, setResolvedHeight] = useState(height);
  const [presetIndex, setPresetIndex] = useState(0);
  const [presetMenuOpen, setPresetMenuOpen] = useState(false);

  const [controlsOpen, setControlsOpen] = useState(false);
  const [ui, setUi] = useState({
    coastlineOpacity: 1.0,
    coastlineSecondaryOpacity: 1.0,
    coastlineColor: '#2c7a5c',
    coastlineSecondaryColor: '#2e8b68',
    ionosphereOpacity: 0.25,
    glowOpacity: 0,
    fireflyOpacity: 0.72,
    autoRotateSpeed: 0.0,
    globeEdgeStrength: 0.17,
    starsOpacity: 1.0,
  });

  const uiRef = useRef(ui);
  useEffect(() => {
    uiRef.current = ui;
  }, [ui]);

  // コンテナ幅に合わせて高さを決定（スマホは正方形、デスクトップは640上限）
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    const update = () => {
      const w = el.clientWidth || 1;
      setResolvedHeight(Math.min(w, 640));
    };
    update();

    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const slider = useMemo(
    () => ({
      item: {
        display: 'grid',
        gridTemplateColumns: '108px 1fr 52px',
        gap: 10,
        alignItems: 'center',
      },
      label: {
        fontSize: 12,
        color: 'rgba(220,240,255,0.92)',
        letterSpacing: '0.04em',
      },
      value: {
        fontSize: 11,
        color: 'rgba(170,220,230,0.88)',
        textAlign: 'right',
        fontVariantNumeric: 'tabular-nums',
      },
      input: {
        width: '100%',
        accentColor: '#58c7bb',
        cursor: 'pointer',
      },
    }),
    []
  );

  useEffect(() => {
    const mountEl = mountRef.current;
    if (!mountEl) return undefined;

    let disposed = false;
    let animationId = 0;
    let resizeObserver = null;

    const scene = new THREE.Scene();
    scene.fog = new THREE.FogExp2(0x01030a, 0.105);

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance',
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    const initWidth = mountEl.clientWidth || 1;
    const initHeight = wrapperRef.current ? wrapperRef.current.clientHeight || initWidth : initWidth;
    renderer.setSize(initWidth, initHeight);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.setClearColor(background, 1);
    mountEl.appendChild(renderer.domElement);

    const camera = new THREE.PerspectiveCamera(
      34,
      initWidth / initHeight,
      0.1,
      100,
    );
    camera.position.set(0, 0, 6.5);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.rotateSpeed = 0.55;
    controls.zoomSpeed = 0.85;
    controls.enablePan = false;
    controls.minDistance = 2.6;
    controls.maxDistance = 8.5;
    controls.autoRotate = false;
    controls.autoRotateSpeed = 0;

    const root = new THREE.Group();
    // 日本が正面に来るように回転（経度137°を+Z軸に向ける）
    root.rotation.y = -(137 * Math.PI) / 180;
    root.rotation.x = 0.2; // 少し上から見下ろす（日本の緯度帯が中央に）
    scene.add(root);

    scene.add(new THREE.AmbientLight(0x35576b, 0.42));

    const hemiLight = new THREE.HemisphereLight(0x254a62, 0x02040a, 0.38);
    scene.add(hemiLight);

    const dirLight = new THREE.DirectionalLight(0x6aa5b2, 0.42);
    dirLight.position.set(4, 2, 5);
    scene.add(dirLight);

    const globeRadius = 1.84;

    const starCount = 900;
    const starPositions = new Float32Array(starCount * 3);
    const starSizes = new Float32Array(starCount);
    for (let i = 0; i < starCount; i += 1) {
      const r = THREE.MathUtils.randFloat(14, 34);
      const theta = THREE.MathUtils.randFloat(0, Math.PI * 2);
      const phi = Math.acos(THREE.MathUtils.randFloatSpread(2));
      starPositions[i * 3 + 0] = r * Math.sin(phi) * Math.cos(theta);
      starPositions[i * 3 + 1] = r * Math.cos(phi);
      starPositions[i * 3 + 2] = r * Math.sin(phi) * Math.sin(theta);
      starSizes[i] = THREE.MathUtils.randFloat(0.8, 3.2);
    }

    const starGeometry = new THREE.BufferGeometry();
    starGeometry.setAttribute('position', new THREE.BufferAttribute(starPositions, 3));
    starGeometry.setAttribute('aSize', new THREE.BufferAttribute(starSizes, 1));

    const starMaterial = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      uniforms: {
        uPixelRatio: { value: Math.min(window.devicePixelRatio || 1, 2) },
        uOpacity: { value: uiRef.current.starsOpacity },
      },
      vertexShader: `
        attribute float aSize;
        uniform float uPixelRatio;
        void main() {
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = aSize * uPixelRatio * (14.0 / -mvPosition.z);
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        uniform float uOpacity;
        void main() {
          vec2 uv = gl_PointCoord - 0.5;
          float d = length(uv);
          float glow = smoothstep(0.5, 0.0, d);
          float core = smoothstep(0.12, 0.0, d);
          float a = (glow + core * 0.6) * uOpacity;
          vec3 col = mix(vec3(0.75, 0.87, 1.0), vec3(1.0), core * 0.5);
          gl_FragColor = vec4(col, a);
        }
      `,
    });
    const stars = new THREE.Points(starGeometry, starMaterial);
    scene.add(stars);

    const globeGeometry = new THREE.SphereGeometry(globeRadius, 96, 96);
    const globeMaterial = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uBaseA: { value: new THREE.Color('#021018') },
        uBaseB: { value: new THREE.Color('#071923') },
        uEdge: { value: new THREE.Color('#0a5f65') },
        uEdgeStrength: { value: uiRef.current.globeEdgeStrength },
      },
      vertexShader: `
        varying vec3 vWorldPosition;
        varying vec3 vNormal;
        varying vec2 vUv;
        void main() {
          vUv = uv;
          vec4 worldPosition = modelMatrix * vec4(position, 1.0);
          vWorldPosition = worldPosition.xyz;
          vNormal = normalize(mat3(modelMatrix) * normal);
          gl_Position = projectionMatrix * viewMatrix * worldPosition;
        }
      `,
      fragmentShader: `
        uniform float uTime;
        uniform vec3 uBaseA;
        uniform vec3 uBaseB;
        uniform vec3 uEdge;
        uniform float uEdgeStrength;
        varying vec3 vWorldPosition;
        varying vec3 vNormal;
        varying vec2 vUv;

        float hash(vec2 p) {
          return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
        }

        float noise(vec2 p) {
          vec2 i = floor(p);
          vec2 f = fract(p);
          float a = hash(i);
          float b = hash(i + vec2(1.0, 0.0));
          float c = hash(i + vec2(0.0, 1.0));
          float d = hash(i + vec2(1.0, 1.0));
          vec2 u = f * f * (3.0 - 2.0 * f);
          return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
        }

        void main() {
          vec3 viewDir = normalize(cameraPosition - vWorldPosition);
          float fresnel = pow(1.0 - max(dot(viewDir, normalize(vNormal)), 0.0), 3.8);
          float n = noise(vUv * vec2(8.0, 4.0) + vec2(uTime * 0.005, 0.0));
          vec3 base = mix(uBaseA, uBaseB, vUv.y * 0.7 + n * 0.16);
          base += uEdge * fresnel * uEdgeStrength;
          gl_FragColor = vec4(base, 1.0);
        }
      `,
    });
    const globe = new THREE.Mesh(globeGeometry, globeMaterial);
    root.add(globe);

    const polyGeometry = new THREE.IcosahedronGeometry(globeRadius * 1.0015, 4);
    const polyMaterial = new THREE.MeshBasicMaterial({
      color: 0x0a2740,
      wireframe: true,
      transparent: true,
      opacity: 0.11,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const polyMesh = new THREE.Mesh(polyGeometry, polyMaterial);
    root.add(polyMesh);

    const subtleGrid = new THREE.Mesh(
      new THREE.SphereGeometry(globeRadius * 1.0025, 32, 22),
      new THREE.MeshBasicMaterial({
        color: 0x0d2c46,
        wireframe: true,
        transparent: true,
        opacity: 0.05,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    );
    root.add(subtleGrid);

    const atmosphere = new THREE.Mesh(
      new THREE.SphereGeometry(globeRadius * 1.024, 64, 64),
      new THREE.ShaderMaterial({
        transparent: true,
        side: THREE.BackSide,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        uniforms: {
          uColor: { value: new THREE.Color('#155d62') },
          uOpacity: { value: uiRef.current.glowOpacity },
        },
        vertexShader: `
          varying vec3 vWorldPosition;
          varying vec3 vNormal;
          void main() {
            vec4 worldPosition = modelMatrix * vec4(position, 1.0);
            vWorldPosition = worldPosition.xyz;
            vNormal = normalize(mat3(modelMatrix) * normal);
            gl_Position = projectionMatrix * viewMatrix * worldPosition;
          }
        `,
        fragmentShader: `
          uniform vec3 uColor;
          uniform float uOpacity;
          varying vec3 vWorldPosition;
          varying vec3 vNormal;
          void main() {
            vec3 viewDir = normalize(cameraPosition - vWorldPosition);
            float fresnel = pow(1.0 - max(dot(viewDir, normalize(vNormal)), 0.0), 2.9);
            gl_FragColor = vec4(uColor, fresnel * uOpacity);
          }
        `,
      }),
    );
    root.add(atmosphere);

    const ionosphere = new THREE.Group();
    root.add(ionosphere);

    function makeLatLine(latDeg) {
      const radius = Math.cos(THREE.MathUtils.degToRad(latDeg)) * globeRadius * 1.03;
      const y = Math.sin(THREE.MathUtils.degToRad(latDeg)) * globeRadius * 1.03;
      const geo = new THREE.TorusGeometry(radius, 0.0032, 8, 180);
      const mat = new THREE.MeshBasicMaterial({
        color: 0x2aaeb7,
        transparent: true,
        opacity: 0.08,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
      const meshObj = new THREE.Mesh(geo, mat);
      meshObj.rotation.x = Math.PI / 2;
      meshObj.position.y = y;
      return meshObj;
    }

    function makeLngLine(lngDeg) {
      const geo = new THREE.TorusGeometry(globeRadius * 1.03, 0.003, 8, 180);
      const mat = new THREE.MeshBasicMaterial({
        color: 0x2aaeb7,
        transparent: true,
        opacity: 0.07,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
      const meshObj = new THREE.Mesh(geo, mat);
      meshObj.rotation.z = Math.PI / 2;
      meshObj.rotation.y = THREE.MathUtils.degToRad(lngDeg);
      return meshObj;
    }

    [-60, -35, -10, 15, 40, 65].forEach((lat) => ionosphere.add(makeLatLine(lat)));
    [-150, -120, -90, -60, -30, 0, 30, 60, 90, 120, 150].forEach((lng) => ionosphere.add(makeLngLine(lng)));

    const coastlineGroup = new THREE.Group();
    root.add(coastlineGroup);

    function latLngToVec3(lat, lng, radius) {
      const latRad = THREE.MathUtils.degToRad(lat);
      const lngRad = THREE.MathUtils.degToRad(lng);
      const x = radius * Math.cos(latRad) * Math.sin(lngRad);
      const y = radius * Math.sin(latRad);
      const z = radius * Math.cos(latRad) * Math.cos(lngRad);
      return new THREE.Vector3(x, y, z);
    }

    function coordsToLine(coords, radius, color, opacity) {
      const points = [];
      for (let i = 0; i < coords.length; i += 1) {
        const pair = coords[i];
        const lng = pair[0];
        const lat = pair[1];
        points.push(latLngToVec3(lat, lng, radius));
      }
      if (points.length < 2) return null;
      const geometry = new THREE.BufferGeometry().setFromPoints(points);
      const material = new THREE.LineBasicMaterial({
        color,
        transparent: true,
        opacity,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
      return new THREE.Line(geometry, material);
    }

    async function loadCoastlines() {
      try {
        const response = await fetch(worldAtlasUrl, { cache: 'force-cache' });
        if (!response.ok) {
          throw new Error(`Failed to fetch coastline data: ${response.status}`);
        }

        const text = await response.text();
        let topology;
        try {
          topology = JSON.parse(text);
        } catch (parseError) {
          throw new Error(
            `Coastline JSON parse failed. Received non-JSON content from ${worldAtlasUrl}. public/data/land-110m.json を配置して worldAtlasUrl をそちらへ向けてください。`
          );
        }

        if (disposed) return;

        const landObject = topology?.objects?.land;
        if (!landObject) {
          throw new Error('topology.objects.land が見つかりません。land-110m.json を確認してください。');
        }

        while (coastlineGroup.children.length > 0) {
          const child = coastlineGroup.children[0];
          coastlineGroup.remove(child);
          child.geometry?.dispose?.();
          child.material?.dispose?.();
        }

        const landFeature = feature(topology, landObject);
        const coastlineMesh = mesh(topology, landObject);

        const addPolygon = (polygon) => {
          polygon.forEach((ring) => {
            const line = coordsToLine(
              ring,
              globeRadius * 1.0065,
              new THREE.Color(uiRef.current.coastlineColor),
              uiRef.current.coastlineOpacity
            );
            if (line) coastlineGroup.add(line);
          });
        };

        if (landFeature) {
          if (landFeature.type === 'FeatureCollection' && Array.isArray(landFeature.features)) {
            landFeature.features.forEach((f) => {
              const geom = f?.geometry;
              if (!geom) return;
              if (geom.type === 'Polygon') addPolygon(geom.coordinates);
              if (geom.type === 'MultiPolygon') geom.coordinates.forEach(addPolygon);
            });
          } else if (landFeature.type === 'Feature' && landFeature.geometry) {
            const geom = landFeature.geometry;
            if (geom.type === 'Polygon') addPolygon(geom.coordinates);
            if (geom.type === 'MultiPolygon') geom.coordinates.forEach(addPolygon);
          }
        }

        if (coastlineMesh && coastlineMesh.coordinates) {
          coastlineMesh.coordinates.forEach((lineString) => {
            const line = coordsToLine(
              lineString,
              globeRadius * 1.0075,
              new THREE.Color(uiRef.current.coastlineSecondaryColor),
              uiRef.current.coastlineSecondaryOpacity
            );
            if (line) coastlineGroup.add(line);
          });
        }
      } catch (error) {
        console.error('[OtohikariGlobe] coastline load error:', error);
      }
    }

    loadCoastlines();

    const fireflyGeometry = new THREE.BufferGeometry();
    fireflyGeometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array([]), 3));
    fireflyGeometry.setAttribute('aSize', new THREE.BufferAttribute(new Float32Array([]), 1));
    fireflyGeometry.setAttribute('aPhase', new THREE.BufferAttribute(new Float32Array([]), 1));

    const preset0 = LIGHT_PRESETS[0];
    const fireflyMaterial = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      uniforms: {
        uTime: { value: 0 },
        uPixelRatio: { value: Math.min(window.devicePixelRatio || 1, 2) },
        uOpacity: { value: preset0.opacity },
        uPulseBase: { value: preset0.pulseBase },
        uPulseAmp: { value: preset0.pulseAmp },
        uPulseSpeed: { value: preset0.pulseSpeed },
        uGlowRadius: { value: preset0.glowRadius },
        uCoreRadius: { value: preset0.coreRadius },
        uOuterColor: { value: new THREE.Vector3(...preset0.outerColor) },
        uInnerColor: { value: new THREE.Vector3(...preset0.innerColor) },
      },
      vertexShader: `
        attribute float aSize;
        attribute float aPhase;
        uniform float uTime;
        uniform float uPixelRatio;
        uniform float uPulseBase;
        uniform float uPulseAmp;
        uniform float uPulseSpeed;
        varying float vAlpha;
        void main() {
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          float pulse = uPulseBase + uPulseAmp * sin(uTime * uPulseSpeed + aPhase);
          vAlpha = pulse;
          gl_PointSize = aSize * pulse * uPixelRatio * (18.0 / -mvPosition.z);
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        uniform float uOpacity;
        uniform float uGlowRadius;
        uniform float uCoreRadius;
        uniform vec3 uOuterColor;
        uniform vec3 uInnerColor;
        varying float vAlpha;
        void main() {
          vec2 uv = gl_PointCoord - 0.5;
          float d = length(uv);
          float glow = smoothstep(uGlowRadius, 0.0, d);
          float core = smoothstep(uCoreRadius, 0.0, d);
          vec3 color = mix(uOuterColor, uInnerColor, core);
          gl_FragColor = vec4(color, glow * vAlpha * uOpacity);
        }
      `,
    });
    fireflyMaterialRef.current = fireflyMaterial;
    const fireflies = new THREE.Points(fireflyGeometry, fireflyMaterial);
    firefliesRef.current = fireflies;
    root.add(fireflies);

    function buildLocationSeeds(byLocation = {}) {
      return Object.values(byLocation)
        .filter((entry) => entry && Number.isFinite(entry.lat) && Number.isFinite(entry.lng) && Number(entry.count) > 0)
        .map((entry) => ({
          lat: entry.lat,
          lng: entry.lng,
          count: Number(entry.count),
        }));
    }

    function updateFireflies(byPref = {}, byLocation = {}) {
      const preset = LIGHT_PRESETS[presetIndexRef.current] || LIGHT_PRESETS[0];
      // byLocation と byPref の両方をマージして使う
      // （lat/lng未登録ユーザーは byPref のみ、登録済みユーザーは byLocation にも入る）
      lastSeedsRef.current = { byPref, byLocation };
      const locationSeeds = buildLocationSeeds(byLocation);
      const prefSeeds = buildPrefectureSeeds(byPref);
      const seeds = [...locationSeeds, ...prefSeeds];

      const positions = [];
      const sizes = [];
      const phases = [];

      seeds.forEach(({ lat, lng }) => {
        const p = latLngToVec3(lat, lng, globeRadius * 1.016);
        positions.push(p.x, p.y, p.z);
        sizes.push(preset.pointSize + Math.random() * preset.sizeRandom);
        phases.push(Math.random() * Math.PI * 2);
      });

      fireflyGeometry.setAttribute(
        'position',
        new THREE.BufferAttribute(new Float32Array(positions), 3)
      );
      fireflyGeometry.setAttribute(
        'aSize',
        new THREE.BufferAttribute(new Float32Array(sizes), 1)
      );
      fireflyGeometry.setAttribute(
        'aPhase',
        new THREE.BufferAttribute(new Float32Array(phases), 1)
      );
      fireflyGeometry.computeBoundingSphere();
    }

    // ─── Preview版：Firestoreではなく静的JSONから全地点を光らせる ───
    async function loadAllStaticLights() {
      try {
        const [japanRes, usRes, worldRes] = await Promise.all([
          fetch('/data/japanCities.json'),
          fetch('/data/usStates.json'),
          fetch('/data/worldCities.json'),
        ]);
        const japanData = await japanRes.json();   // { "北海道": [{name, lat, lng}, ...], ... }
        const usData = await usRes.json();          // { "Alabama": [{name, lat, lng}, ...], ... }
        const worldData = await worldRes.json();    // [{name, lat, lng}, ...]

        // 全地点を byLocation 形式に変換
        const byLocation = {};
        let idx = 0;

        // 日本全市区町村
        Object.values(japanData).forEach((cities) => {
          cities.forEach((city) => {
            byLocation[`jp_${idx++}`] = { lat: city.lat, lng: city.lng, count: 1 };
          });
        });

        // アメリカ全州
        Object.values(usData).forEach((states) => {
          states.forEach((state) => {
            byLocation[`us_${idx++}`] = { lat: state.lat, lng: state.lng, count: 1 };
          });
        });

        // 海外主要都市
        worldData.forEach((city) => {
          byLocation[`world_${idx++}`] = { lat: city.lat, lng: city.lng, count: 1 };
        });

        console.log(`[PreviewOtohikariGlobe] Loaded ${idx} light points`);
        updateFireflies({}, byLocation);
      } catch (error) {
        console.error('[PreviewOtohikariGlobe] static data fetch error:', error);
        updateFireflies({}, {});
      }
    }

    loadAllStaticLights();

    // ポーリング不要（静的データなので一度読めばOK）

    const resonanceShell = new THREE.Mesh(
      new THREE.SphereGeometry(globeRadius * 1.055, 56, 56),
      new THREE.ShaderMaterial({
        transparent: true,
        depthWrite: false,
        side: THREE.BackSide,
        blending: THREE.AdditiveBlending,
        uniforms: {
          uTime: { value: 0 },
        },
        vertexShader: `
          uniform float uTime;
          varying float vPulse;
          varying vec3 vWorldPosition;
          varying vec3 vNormal;
          void main() {
            vec3 p = position;
            float pulse = sin(uTime * 1.15 + position.y * 2.5) * 0.01;
            p *= 1.0 + pulse;
            vPulse = pulse;
            vec4 worldPosition = modelMatrix * vec4(p, 1.0);
            vWorldPosition = worldPosition.xyz;
            vNormal = normalize(mat3(modelMatrix) * normal);
            gl_Position = projectionMatrix * viewMatrix * worldPosition;
          }
        `,
        fragmentShader: `
          varying float vPulse;
          varying vec3 vWorldPosition;
          varying vec3 vNormal;
          void main() {
            vec3 viewDir = normalize(cameraPosition - vWorldPosition);
            float fresnel = pow(1.0 - max(dot(viewDir, normalize(vNormal)), 0.0), 2.4);
            float alpha = fresnel * (0.055 + abs(vPulse) * 2.0);
            gl_FragColor = vec4(0.10, 0.45, 0.42, alpha);
          }
        `,
      }),
    );
    root.add(resonanceShell);

    // ===== Ionosphere triangular grid resonance =====
    const gridShellGroup = new THREE.Group();
    root.add(gridShellGroup);

    function fibonacciSphere(samples, radius) {
      const points = [];
      const offset = 2 / samples;
      const increment = Math.PI * (3 - Math.sqrt(5));
      for (let i = 0; i < samples; i += 1) {
        const y = i * offset - 1 + offset / 2;
        const r = Math.sqrt(Math.max(0, 1 - y * y));
        const phi = i * increment;
        const x = Math.cos(phi) * r;
        const z = Math.sin(phi) * r;
        points.push(new THREE.Vector3(x * radius, y * radius, z * radius));
      }
      return points;
    }

    function buildTriangleGrid(points, neighborCount = 3) {
      const edgeSet = new Set();
      const triangles = [];
      const triSet = new Set();
      const neighborsList = [];

      for (let i = 0; i < points.length; i += 1) {
        const scored = [];
        for (let j = 0; j < points.length; j += 1) {
          if (i === j) continue;
          scored.push({ j, d: points[i].distanceToSquared(points[j]) });
        }
        scored.sort((a, b) => a.d - b.d);
        neighborsList.push(scored.slice(0, neighborCount).map((v) => v.j));
      }

      for (let i = 0; i < points.length; i += 1) {
        const neighbors = neighborsList[i];
        for (let a = 0; a < neighbors.length; a += 1) {
          for (let b = a + 1; b < neighbors.length; b += 1) {
            const j = neighbors[a];
            const k = neighbors[b];
            const tri = [i, j, k].sort((m, n) => m - n);
            const triKey = tri.join('-');
            if (!triSet.has(triKey)) {
              triSet.add(triKey);
              triangles.push(tri);
            }
            [[i, j], [j, k], [k, i]].forEach(([u, v]) => {
              edgeSet.add(u < v ? `${u}-${v}` : `${v}-${u}`);
            });
          }
        }
      }

      return {
        edges: Array.from(edgeSet).map((key) => key.split('-').map(Number)),
        triangles,
      };
    }

    const gridRadius = globeRadius * 1.08;
    const gridPoints = fibonacciSphere(84, gridRadius);
    const gridData = buildTriangleGrid(gridPoints, 3);

    const baseGridPositions = [];
    gridData.edges.forEach(([a, b]) => {
      const p1 = gridPoints[a];
      const p2 = gridPoints[b];
      baseGridPositions.push(p1.x, p1.y, p1.z, p2.x, p2.y, p2.z);
    });

    const baseGridGeometry = new THREE.BufferGeometry();
    baseGridGeometry.setAttribute(
      'position',
      new THREE.BufferAttribute(new Float32Array(baseGridPositions), 3)
    );
    const baseGridMaterial = new THREE.LineBasicMaterial({
      color: 0x5ea8ff,
      transparent: true,
      opacity: 0.08,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const baseGridLines = new THREE.LineSegments(baseGridGeometry, baseGridMaterial);
    gridShellGroup.add(baseGridLines);

    const pulseGridGroup = new THREE.Group();
    gridShellGroup.add(pulseGridGroup);

    function createPulsePatch() {
      while (pulseGridGroup.children.length > 0) {
        const child = pulseGridGroup.children[0];
        pulseGridGroup.remove(child);
        child.geometry?.dispose?.();
        child.material?.dispose?.();
      }

      const totalTriangles = gridData.triangles.length;
      const targetCount = Math.max(20, Math.min(totalTriangles, Math.floor(totalTriangles * 0.42)));
      const selected = [...gridData.triangles].sort(() => Math.random() - 0.5).slice(0, targetCount);

      const edgePositions = [];
      const triPositions = [];
      selected.forEach(([a, b, c]) => {
        const p1 = gridPoints[a];
        const p2 = gridPoints[b];
        const p3 = gridPoints[c];

        edgePositions.push(
          p1.x, p1.y, p1.z, p2.x, p2.y, p2.z,
          p2.x, p2.y, p2.z, p3.x, p3.y, p3.z,
          p3.x, p3.y, p3.z, p1.x, p1.y, p1.z,
        );

        if (Math.random() < 0.16) {
          triPositions.push(
            p1.x, p1.y, p1.z,
            p2.x, p2.y, p2.z,
            p3.x, p3.y, p3.z,
          );
        }
      });

      const pulseLineGeometry = new THREE.BufferGeometry();
      pulseLineGeometry.setAttribute(
        'position',
        new THREE.BufferAttribute(new Float32Array(edgePositions), 3)
      );
      const pulseLineMaterial = new THREE.LineBasicMaterial({
        color: 0x7ec3ff,
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
      pulseGridGroup.add(new THREE.LineSegments(pulseLineGeometry, pulseLineMaterial));

      if (triPositions.length > 0) {
        const pulseTriGeometry = new THREE.BufferGeometry();
        pulseTriGeometry.setAttribute(
          'position',
          new THREE.BufferAttribute(new Float32Array(triPositions), 3)
        );
        pulseTriGeometry.computeVertexNormals();
        const pulseTriMaterial = new THREE.MeshBasicMaterial({
          color: 0x6fb9ff,
          transparent: true,
          opacity: 0,
          side: THREE.DoubleSide,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
        });
        pulseGridGroup.add(new THREE.Mesh(pulseTriGeometry, pulseTriMaterial));
      }
    }

    createPulsePatch();

    // ===== 彗星 / 流れ星 =====
    const COMET_TAIL_LENGTH = 60;
    const cometGeometry = new THREE.BufferGeometry();
    const cometPositions = new Float32Array(COMET_TAIL_LENGTH * 3);
    const cometAlphas = new Float32Array(COMET_TAIL_LENGTH);
    cometGeometry.setAttribute('position', new THREE.BufferAttribute(cometPositions, 3));
    cometGeometry.setAttribute('aAlpha', new THREE.BufferAttribute(cometAlphas, 1));

    const cometMaterial = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      uniforms: {
        uOpacity: { value: 0 },
      },
      vertexShader: `
        attribute float aAlpha;
        varying float vAlpha;
        void main() {
          vAlpha = aAlpha;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform float uOpacity;
        varying float vAlpha;
        void main() {
          gl_FragColor = vec4(0.75, 0.95, 1.0, vAlpha * uOpacity);
        }
      `,
    });
    const cometLine = new THREE.Line(cometGeometry, cometMaterial);
    scene.add(cometLine);

    // 彗星の先端（光る点）
    const cometHeadGeometry = new THREE.BufferGeometry();
    cometHeadGeometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(3), 3));
    const cometHeadMaterial = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      uniforms: {
        uOpacity: { value: 0 },
        uPixelRatio: { value: Math.min(window.devicePixelRatio || 1, 2) },
      },
      vertexShader: `
        uniform float uPixelRatio;
        void main() {
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = 4.0 * uPixelRatio * (18.0 / -mvPosition.z);
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        uniform float uOpacity;
        void main() {
          vec2 uv = gl_PointCoord - 0.5;
          float d = length(uv);
          float glow = smoothstep(0.5, 0.0, d);
          float core = smoothstep(0.08, 0.0, d);
          vec3 color = mix(vec3(0.6, 0.9, 1.0), vec3(1.0), core);
          gl_FragColor = vec4(color, glow * uOpacity);
        }
      `,
    });
    const cometHead = new THREE.Points(cometHeadGeometry, cometHeadMaterial);
    scene.add(cometHead);

    const comet = {
      active: false,
      nextSpawnTime: THREE.MathUtils.randFloat(8, 18),
      startTime: 0,
      duration: 0,
      // 軌道パラメータ
      startPos: new THREE.Vector3(),
      endPos: new THREE.Vector3(),
      controlA: new THREE.Vector3(),
      controlB: new THREE.Vector3(),
    };

    function spawnComet(elapsed) {
      comet.active = true;
      comet.startTime = elapsed;
      comet.duration = THREE.MathUtils.randFloat(2.5, 7.0);

      // 宇宙の遠くからスーッと直線的に通り過ぎる流れ星
      // 地球の手前を横切る（衝突しない安全な軌道）
      const far = THREE.MathUtils.randFloat(7, 12);
      const angle = THREE.MathUtils.randFloat(0, Math.PI * 2);
      const dx = Math.cos(angle);
      const dy = Math.sin(angle) * 0.6;
      // Z を手前側に固定して地球に突っ込まない
      const z = THREE.MathUtils.randFloat(globeRadius * 2.5, globeRadius * 4.0);
      const yOffset = THREE.MathUtils.randFloat(-1.5, 1.5);

      comet.startPos.set(-dx * far, -dy * far + yOffset, z);
      comet.endPos.set(dx * far, dy * far + yOffset, z);

      // ほぼ直線（ごく僅かなカーブ）
      const bend = THREE.MathUtils.randFloat(-0.2, 0.2);
      comet.controlA.lerpVectors(comet.startPos, comet.endPos, 0.33);
      comet.controlA.y += bend;
      comet.controlB.lerpVectors(comet.startPos, comet.endPos, 0.66);
      comet.controlB.y += bend * 0.5;
    }

    function cubicBezier(t, p0, p1, p2, p3, out) {
      const u = 1 - t;
      const uu = u * u;
      const uuu = uu * u;
      const tt = t * t;
      const ttt = tt * t;
      out.x = uuu * p0.x + 3 * uu * t * p1.x + 3 * u * tt * p2.x + ttt * p3.x;
      out.y = uuu * p0.y + 3 * uu * t * p1.y + 3 * u * tt * p2.y + ttt * p3.y;
      out.z = uuu * p0.z + 3 * uu * t * p1.z + 3 * u * tt * p2.z + ttt * p3.z;
      return out;
    }

    function updateComet(elapsed) {
      if (!comet.active) {
        if (elapsed >= comet.nextSpawnTime) {
          spawnComet(elapsed);
        } else {
          cometMaterial.uniforms.uOpacity.value = 0;
          cometHeadMaterial.uniforms.uOpacity.value = 0;
          return;
        }
      }

      const progress = (elapsed - comet.startTime) / comet.duration;

      if (progress >= 1) {
        comet.active = false;
        comet.nextSpawnTime = elapsed + THREE.MathUtils.randFloat(10, 25);
        cometMaterial.uniforms.uOpacity.value = 0;
        cometHeadMaterial.uniforms.uOpacity.value = 0;
        return;
      }

      // フェードイン/アウト
      let opacity = 1;
      if (progress < 0.15) opacity = progress / 0.15;
      else if (progress > 0.75) opacity = (1 - progress) / 0.25;
      cometMaterial.uniforms.uOpacity.value = opacity * 0.7;
      cometHeadMaterial.uniforms.uOpacity.value = opacity;

      // テールの各ポイント位置を更新
      const posAttr = cometGeometry.getAttribute('position');
      const alphaAttr = cometGeometry.getAttribute('aAlpha');
      const tmpVec = new THREE.Vector3();

      for (let i = 0; i < COMET_TAIL_LENGTH; i++) {
        const tailT = progress - (i / COMET_TAIL_LENGTH) * 0.12;
        const clampedT = Math.max(0, Math.min(1, tailT));
        cubicBezier(clampedT, comet.startPos, comet.controlA, comet.controlB, comet.endPos, tmpVec);
        posAttr.setXYZ(i, tmpVec.x, tmpVec.y, tmpVec.z);
        alphaAttr.setX(i, Math.max(0, 1 - (i / COMET_TAIL_LENGTH)));
      }
      posAttr.needsUpdate = true;
      alphaAttr.needsUpdate = true;

      // 先端位置を更新
      cubicBezier(progress, comet.startPos, comet.controlA, comet.controlB, comet.endPos, tmpVec);
      const headAttr = cometHeadGeometry.getAttribute('position');
      headAttr.setXYZ(0, tmpVec.x, tmpVec.y, tmpVec.z);
      headAttr.needsUpdate = true;
    }

    const gridPulse = {
      warmupSec: 2,
      intervalSec: 5,
      visibleSec: 2.5,
      enabled: true,
      lineMaxOpacity: 0.34,
      triMaxOpacity: 0.16,
      baseOpacity: 0.08,
      lastCycle: -1,
    };

    const startTime = performance.now();

    const handleResize = () => {
      if (!mountEl || disposed) return;
      const width = mountEl.clientWidth || 1;
      const h = wrapperRef.current ? wrapperRef.current.clientHeight || width : width;
      renderer.setSize(width, h);
      camera.aspect = width / h;
      camera.updateProjectionMatrix();
    };

    resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(mountEl);

    const animate = () => {
      if (disposed) return;
      animationId = window.requestAnimationFrame(animate);
      const elapsed = (performance.now() - startTime) / 1000;
      const state = uiRef.current;

      controls.autoRotateSpeed = autoRotate ? state.autoRotateSpeed : 0;
      controls.update();

      root.rotation.y += 0.0006;
      stars.rotation.y += 0.00008;

      globeMaterial.uniforms.uTime.value = elapsed;
      globeMaterial.uniforms.uEdgeStrength.value = state.globeEdgeStrength;
      atmosphere.material.uniforms.uOpacity.value = state.glowOpacity;
      starMaterial.uniforms.uOpacity.value = state.starsOpacity;
      fireflyMaterial.uniforms.uTime.value = elapsed;
      resonanceShell.material.uniforms.uTime.value = elapsed;

      const pulse = state.ionosphereOpacity + (Math.sin(elapsed * 1.2) * 0.5 + 0.5) * 0.03;
      ionosphere.children.forEach((child, index) => {
        child.material.opacity = pulse * (index % 2 === 0 ? 1.0 : 0.82);
      });

      coastlineGroup.children.forEach((child, index) => {
        if (!child.material) return;
        const isPrimary = index % 2 === 0;
        child.material.opacity = isPrimary ? state.coastlineOpacity : state.coastlineSecondaryOpacity;
        child.material.color.set(isPrimary ? state.coastlineColor : state.coastlineSecondaryColor);
      });

      const afterWarmup = elapsed - gridPulse.warmupSec;
      if (gridPulse.enabled && afterWarmup >= 0) {
        const cycle = Math.floor(afterWarmup / gridPulse.intervalSec);
        const cycleT = afterWarmup % gridPulse.intervalSec;

        if (cycle !== gridPulse.lastCycle && cycleT < 0.2) {
          gridPulse.lastCycle = cycle;
          createPulsePatch();
        }

        let pulseStrength = 0;
        if (cycleT < gridPulse.visibleSec) {
          const x = cycleT / gridPulse.visibleSec;
          if (x < 0.23) {
            pulseStrength = x / 0.23;
          } else if (x < 0.63) {
            pulseStrength = 1.0 - Math.sin(((x - 0.23) / 0.40) * Math.PI) * 0.08;
          } else {
            pulseStrength = Math.max(0, 1 - (x - 0.63) / 0.37);
          }
        }

        baseGridMaterial.opacity = gridPulse.baseOpacity;
        pulseGridGroup.children.forEach((child) => {
          if (child.type === 'LineSegments') {
            child.material.opacity = gridPulse.lineMaxOpacity * pulseStrength;
          } else {
            child.material.opacity = gridPulse.triMaxOpacity * pulseStrength;
          }
        });
      } else {
        baseGridMaterial.opacity = 0.05;
        pulseGridGroup.children.forEach((child) => {
          child.material.opacity = 0;
        });
      }

      updateComet(elapsed);

      renderer.render(scene, camera);
    };

    handleResize();
    animate();

    return () => {
      disposed = true;
      window.cancelAnimationFrame(animationId);
      if (resizeObserver) resizeObserver.disconnect();
      // Preview版: ポーリングなしのため削除済み
      controls.dispose();
      firefliesRef.current = null;

      scene.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose?.();
        if (obj.material) {
          if (Array.isArray(obj.material)) {
            obj.material.forEach((m) => m.dispose?.());
          } else {
            obj.material.dispose?.();
          }
        }
      });

      renderer.dispose();
      if (renderer.domElement.parentNode === mountEl) {
        mountEl.removeChild(renderer.domElement);
      }
    };
  // resolvedHeight は依存配列から除外（ResizeObserverで動的に対応するため、シーン全体の再構築を防ぐ）
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRotate, background, worldAtlasUrl, db, trackId, aggregateCollectionName, pollSec, burstMin]);

  // プリセット切替時にシェーダーuniformsとジオメトリを再構築
  useEffect(() => {
    presetIndexRef.current = presetIndex;
    const mat = fireflyMaterialRef.current;
    if (!mat) return;
    const preset = LIGHT_PRESETS[presetIndex] || LIGHT_PRESETS[0];
    mat.uniforms.uOpacity.value = preset.opacity;
    mat.uniforms.uPulseBase.value = preset.pulseBase;
    mat.uniforms.uPulseAmp.value = preset.pulseAmp;
    mat.uniforms.uPulseSpeed.value = preset.pulseSpeed;
    mat.uniforms.uGlowRadius.value = preset.glowRadius;
    mat.uniforms.uCoreRadius.value = preset.coreRadius;
    mat.uniforms.uOuterColor.value.set(...preset.outerColor);
    mat.uniforms.uInnerColor.value.set(...preset.innerColor);
    mat.blending = preset.additive ? THREE.AdditiveBlending : THREE.NormalBlending;
    mat.needsUpdate = true;
    // サイズを再生成するために updateFireflies を呼び直す
    const { byPref, byLocation } = lastSeedsRef.current;
    if (firefliesRef.current) {
      const geo = firefliesRef.current.geometry;
      const locationSeeds = Object.values(byLocation || {})
        .filter((e) => e && Number.isFinite(e.lat) && Number.isFinite(e.lng) && Number(e.count) > 0)
        .map((e) => ({ lat: e.lat, lng: e.lng }));
      const prefSeeds = Object.entries(byPref || {})
        .filter(([, c]) => Number(c) > 0)
        .map(([name]) => {
          const coords = PREFECTURE_COORDS[name];
          return coords ? { lat: coords.lat, lng: coords.lng } : null;
        })
        .filter(Boolean);
      const seeds = [...locationSeeds, ...prefSeeds];
      const sizes = seeds.map(() => preset.pointSize + Math.random() * preset.sizeRandom);
      geo.setAttribute('aSize', new THREE.BufferAttribute(new Float32Array(sizes), 1));
      geo.computeBoundingSphere();
    }
  }, [presetIndex]);

  const updateNumber = (key) => (e) => {
    setUi((prev) => ({ ...prev, [key]: Number(e.target.value) }));
  };

  const updateColor = (key) => (e) => {
    setUi((prev) => ({ ...prev, [key]: e.target.value }));
  };

  return (
    <div
      ref={wrapperRef}
      style={{
        position: 'relative',
        width: '100%',
        maxWidth: 1280,
        margin: '0 auto',
        height: resolvedHeight,
        minHeight: 320,
        borderRadius: 24,
        overflow: 'hidden',
        background: 'radial-gradient(circle at 50% 28%, rgba(7,16,28,0.55) 0%, rgba(1,3,10,1) 62%)',
        boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
      }}
    >
      <div ref={mountRef} style={{ width: '100%', height: '100%' }} />

      {/* OTOHIKARI ヘッダー */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: 0,
          padding: '18px 20px',
          background: 'linear-gradient(to bottom, rgba(1,3,10,0.85) 0%, rgba(1,3,10,0.3) 70%, rgba(1,3,10,0) 100%)',
          pointerEvents: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
        }}
      >
        {/* 音符アイコン */}
        <div style={{
          width: 38,
          height: 38,
          borderRadius: '50%',
          background: 'radial-gradient(circle at 40% 38%, #6ee6a0, #3aad6a)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 0 12px rgba(110,230,160,0.3)',
          flexShrink: 0,
        }}>
          <span style={{ fontSize: 18, color: '#fff', lineHeight: 1 }}>♪</span>
        </div>
        <div>
          <div style={{
            fontFamily: "'Cinzel', serif",
            fontSize: 18,
            fontWeight: 400,
            color: '#c0f0d8',
            letterSpacing: '5px',
            lineHeight: 1.2,
          }}>
            OTOHIKARI
          </div>
          <div style={{
            fontFamily: "'Zen Kaku Gothic New', sans-serif",
            fontSize: 10,
            fontWeight: 300,
            color: 'rgba(192,240,216,0.45)',
            letterSpacing: '2px',
            marginTop: 2,
          }}>
            光の音柱 ー マスターマインドメンバーズ
          </div>
        </div>
      </div>

      {/* LISTENING STATUS オーバーレイ */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          padding: '32px 16px 8px',
          background: 'linear-gradient(to top, rgba(1,3,10,0.92) 0%, rgba(1,3,10,0.4) 60%, rgba(1,3,10,0) 100%)',
          pointerEvents: 'none',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-end',
          gap: 32,
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontFamily: "'Zen Kaku Gothic New', sans-serif",
            fontSize: 9,
            fontWeight: 300,
            letterSpacing: '1.5px',
            color: 'rgba(192,240,216,0.4)',
            marginBottom: 4,
            textTransform: 'uppercase',
          }}>
            LISTENING NOW
          </div>
          <div style={{
            fontFamily: "'Cinzel', serif",
            fontSize: 22,
            fontWeight: 700,
            color: '#7fefb0',
            lineHeight: 1,
            textShadow: '0 0 8px rgba(127,239,176,0.5)',
          }}>
            {currentListeners.toLocaleString()}
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontFamily: "'Zen Kaku Gothic New', sans-serif",
            fontSize: 9,
            fontWeight: 300,
            letterSpacing: '1.5px',
            color: 'rgba(192,240,216,0.4)',
            marginBottom: 4,
            textTransform: 'uppercase',
          }}>
            TODAY
          </div>
          <div style={{
            fontFamily: "'Cinzel', serif",
            fontSize: 22,
            fontWeight: 700,
            color: '#7fefb0',
            lineHeight: 1,
            textShadow: '0 0 8px rgba(127,239,176,0.5)',
          }}>
            {todayListeners.toLocaleString()}
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontFamily: "'Zen Kaku Gothic New', sans-serif",
            fontSize: 9,
            fontWeight: 300,
            letterSpacing: '1.5px',
            color: 'rgba(192,240,216,0.4)',
            marginBottom: 4,
            textTransform: 'uppercase',
          }}>
            TARGET SCHUMANN
          </div>
          <div style={{
            fontFamily: "'Cinzel', serif",
            fontSize: 22,
            fontWeight: 700,
            color: '#7fefb0',
            lineHeight: 1,
            textShadow: '0 0 8px rgba(127,239,176,0.5)',
          }}>
            7.92<span style={{ fontSize: 16, fontWeight: 500, marginLeft: 3, opacity: 0.8 }}>Hz</span>
          </div>
        </div>
      </div>

      {isAdmin && (
        <div style={{ position: 'absolute', right: 12, top: 12 }}>
          <button
            type="button"
            onClick={() => setPresetMenuOpen((v) => !v)}
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: presetMenuOpen ? 'rgba(60,160,140,0.4)' : 'rgba(10,20,30,0.6)',
              border: presetMenuOpen ? '1px solid rgba(127,239,176,0.5)' : '1px solid rgba(80,120,140,0.25)',
              color: '#c0f0d8',
              fontSize: 14,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backdropFilter: 'blur(8px)',
            }}
          >
            ⚙
          </button>
          {presetMenuOpen && (
            <div
              style={{
                position: 'absolute',
                right: 0,
                top: 38,
                background: 'rgba(5,12,20,0.92)',
                border: '1px solid rgba(96,172,177,0.25)',
                borderRadius: 10,
                padding: '6px 0',
                backdropFilter: 'blur(14px)',
                minWidth: 120,
                boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
              }}
            >
              {LIGHT_PRESETS.map((p, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => { setPresetIndex(i); setPresetMenuOpen(false); }}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '8px 16px',
                    fontSize: 12,
                    fontFamily: "'Zen Kaku Gothic New', sans-serif",
                    fontWeight: presetIndex === i ? 700 : 300,
                    color: presetIndex === i ? '#7fefb0' : 'rgba(192,240,216,0.6)',
                    background: presetIndex === i ? 'rgba(60,160,140,0.2)' : 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    letterSpacing: '0.5px',
                  }}
                >
                  {p.name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* コントロールパネルは一時的にコメントアウト
      {showDebugControls && (
        <div
          style={{
            position: 'absolute',
            left: 16,
            bottom: 16,
            width: 360,
            maxWidth: 'calc(100% - 32px)',
            borderRadius: 18,
            background: 'linear-gradient(180deg, rgba(7,18,28,0.88), rgba(5,12,20,0.92))',
            border: '1px solid rgba(96, 172, 177, 0.22)',
            backdropFilter: 'blur(14px)',
            boxShadow: '0 12px 28px rgba(0,0,0,0.35)',
            color: '#e7f8ff',
            overflow: 'hidden',
          }}
        >
          <button
            type="button"
            onClick={() => setControlsOpen((v) => !v)}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 14px',
              background: 'transparent',
              border: 'none',
              color: 'inherit',
              cursor: 'pointer',
              fontSize: 13,
              letterSpacing: '0.08em',
              fontWeight: 700,
            }}
          >
            <span>OTOHIKARI CONTROL</span>
            <span style={{ opacity: 0.8 }}>{controlsOpen ? '−' : '+'}</span>
          </button>

          {controlsOpen && (
            <div style={{ padding: '0 14px 14px', display: 'grid', gap: 12 }}>
              <div style={{ fontSize: 11, color: 'rgba(150,210,220,0.8)', letterSpacing: '0.05em' }}>
                LOOK & ATMOSPHERE
              </div>

              <div style={slider.item}>
                <div style={slider.label}>Glow</div>
                <input type="range" min="0" max="0.4" step="0.005" value={ui.glowOpacity} onChange={updateNumber('glowOpacity')} style={slider.input} />
                <div style={slider.value}>{ui.glowOpacity.toFixed(3)}</div>
              </div>

              <div style={slider.item}>
                <div style={slider.label}>Firefly</div>
                <input type="range" min="0" max="1.5" step="0.01" value={ui.fireflyOpacity} onChange={updateNumber('fireflyOpacity')} style={slider.input} />
                <div style={slider.value}>{ui.fireflyOpacity.toFixed(2)}</div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 4 }}>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: 11, color: 'rgba(170,220,230,0.88)' }}>Coast main color</span>
                  <input type="color" value={ui.coastlineColor} onChange={updateColor('coastlineColor')} style={{ width: '100%', height: 36, border: 'none', background: 'transparent', cursor: 'pointer' }} />
                </label>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: 11, color: 'rgba(170,220,230,0.88)' }}>Coast sub color</span>
                  <input type="color" value={ui.coastlineSecondaryColor} onChange={updateColor('coastlineSecondaryColor')} style={{ width: '100%', height: 36, border: 'none', background: 'transparent', cursor: 'pointer' }} />
                </label>
              </div>
            </div>
          )}
        </div>
      )}
      */}
    </div>
  );
}
